import { PaApi, baseOpts, processSuccess } from '@/services/PaApi'

const defaultSendAt = () => {
  return (new Date().toJSON().slice(0, 19))
}

export const EmailModule = {
  namespaced: true,

  state: {
    attachment: {},
    email: {
      attachments: [],
      confirmed_by: {},
      confirmation_requested_by: {},
      newsletter_type: {
        attributes: {}
      },
      provider: {},
      puzzles: []
    },
    emailRecipient: {
      queued_at: new Date().toUTCString(),
      send_at: new Date().toUTCString()
    },
    emailRecipients: [],
    stats: {}
  },

  getters: {
    isNewsletter: state => {
      return (
        (state.email.type === 'newsletter') ||
        (state.email.type === 'full_newsletter')
      )
    },

    modelPath: state => {
      let emailType = (
        (state.email.type === 'newsletter') ||
        (state.email.type === 'full_newsletter')
      ) ? 'newsletters' : 'targeted_emails'

      return emailType
    },

    isQueued: state => {
      return state.email.send_at && !state.email.sent_at
    },

    isNotQueued: state => {
      return !this.isQueued
    }
  },

  actions: {
    async getAdminEmail ({ commit, state }, emailId) {
      commit('setLoading', true, { root: true })

      await PaApi().get(
        '/admin/targeted_emails/' + emailId
      ).then((resp) => {
        commit('storeEmail', resp)
      }).finally(() => {
        commit('setLoading', false, { root: true })
      })
    },

    async getEmails ({ commit, state }) {
      commit('setLoading', true, { root: true })

      await PaApi().get(
        '/admin/targeted_emails/'
      ).then((resp) => {
        commit('storeEmails', resp)
      }).finally(() => {
        commit('setLoading', false, { root: true })
      })
    },

    async getNewsletter ({ commit, state }, newsletterId) {
      commit('setLoading', true, { root: true })

      await PaApi().get(
        '/newsletters/' + newsletterId
      ).then((resp) => {
        commit('storeEmail', resp)
      }).finally(() => {
        commit('setLoading', false, { root: true })
      })
    },

    async getAdminNewsletter ({ commit, state }, newsletterId) {
      commit('setLoading', true, { root: true })

      await PaApi().get(
        '/admin/newsletters/' + newsletterId
      ).then((resp) => {
        commit('storeEmail', resp)
      }).finally(() => {
        commit('setLoading', false, { root: true })
      })
    },

    async getAttachment ({ commit, state }, params) {
      commit('setLoading', true, { root: true })

      await PaApi().get(
        '/admin/' + [
          params.emailType + 's',
          params.emailId,
          'attachments',
          params.attachmentId
        ].join('/')
      ).then((resp) => {
        commit('storeAttachment', resp)
      }).finally(() => {
        commit('setLoading', false, { root: true })
      })
    },

    async getStats ({ commit, state }, params) {
      commit('setLoading', true, { root: true })

      await PaApi().get(
        '/admin/' + [
          params.emailType + 's',
          params.emailId,
          'stats'
        ].join('/')
      ).then((resp) => {
        commit('storeStats', resp)
      }).finally(() => {
        commit('setLoading', false, { root: true })
      })
    },

    async getStatsByStatus ({ commit, state }, params) {
      commit('setLoading', true, { root: true })

      const url = '/admin/' + [
        params.emailType + 's',
        params.emailId,
        'status',
        params.status
      ].join('/')

      await PaApi().get(
        url
      ).then((resp) => {
        commit('storeEmailRecipients', resp)
      }).finally(() => {
        commit('setLoading', false, { root: true })
      })
    },

    async getNewEmail ({ commit, state }) {
      commit('setLoading', true, { root: true })

      await PaApi().get(
        '/admin/new/targeted_email'
      ).then((resp) => {
        commit('storeEmail', resp)
      }).finally(() => {
        commit('setLoading', false, { root: true })
      })
    },

    async getNewNewsletter ({ commit, state }) {
      commit('setLoading', true, { root: true })

      await PaApi().get(
        '/admin/new/newsletter'
      ).then((resp) => {
        commit('storeEmail', resp)
      }).finally(() => {
        commit('setLoading', false, { root: true })
      })
    },

    async createEmail ({ commit, state }, email) {
      commit('setLoading', true, { root: true })

      await PaApi().post(
        '/admin/targeted_emails', email
      ).then((resp) => {
        commit('storeEmail', resp)
      }).finally(() => {
        commit('setLoading', false, { root: true })
      })
    },

    async updateEmail ({ commit, state }, email) {
      commit('setLoading', true, { root: true })

      await PaApi().put(
        `/admin/targeted_emails/${email.get('targeted_email[id]')}`, email
      ).then((resp) => {
        commit('storeEmail', resp)
      }).finally(() => {
        commit('setLoading', false, { root: true })
      })
    },

    async createNewsletter ({ commit, state }, newsletter) {
      commit('setLoading', true, { root: true })

      await PaApi().post(
        '/admin/newsletters', newsletter
      ).then((resp) => {
        commit('storeEmail', resp)
      }).finally(() => {
        commit('setLoading', false, { root: true })
      })
    },

    async updateNewsletter ({ commit, state }, newsletter) {
      commit('setLoading', true, { root: true })

      await PaApi().put(
        `/admin/newsletters/${newsletter.get('newsletter[id]')}`, newsletter
      ).then((resp) => {
        commit('storeEmail', resp)
      }).finally(() => {
        commit('setLoading', false, { root: true })
      })
    },

    async confirmEmail ({ commit, state }, email) {
      let controller = 'newsletters'
      switch (email.emailType) {
        case 'targeted_emails':
          controller = email.emailType
      }

      await PaApi().post(
        `/admin/${controller}/${email.id}/confirm`
      ).then((resp) => {
        commit('storeEmail', resp)
      }).finally(() => {
        commit('setLoading', false, { root: true })
      })
    },

    async publishEmail ({ commit, state }, emailId) {
      commit('setLoading', true, { root: true })

      await PaApi().post(
        `/admin/targeted_emails/${emailId}/publish`
      ).then((resp) => {
        commit('storeEmail', resp)
      }).finally(() => {
        commit('setLoading', false, { root: true })
      })
    },

    async publishNewsletter ({ commit, state }, emailId) {
      commit('setLoading', true, { root: true })

      await PaApi().post(
        `/admin/newsletters/${emailId}/publish`
      ).then((resp) => {
        commit('storeEmail', resp)
      }).finally(() => {
        commit('setLoading', false, { root: true })
      })
    },

    async getEmailRecipient ({ commit, state }, params) {
      commit('setLoading', true, { root: true })

      const url = '/admin' + (
        this.isNewsletter ? '/newsletters/' + params.newsletterId + '/recipients/' : '/email_recipients/'
      ) + params.recipientId

      await PaApi().get(
        url
      ).then((resp) => {
        commit('storeEmailRecipient', resp)
      }).finally(() => {
        commit('setLoading', false, { root: true })
      })
    },

    async dequeueNewsletterRecipients ({ commit, state }, params) {
      commit('setLoading', true, { root: true })

      await PaApi().delete(
        `/admin/newsletters/${params.emailId}/dequeue_recipient/${params.status}`
      ).then((resp) => {
        commit('storeEmailRecipient', resp)
      }).finally(() => {
        commit('setLoading', false, { root: true })
      })
    },

    async deleteNewsletterRecipientsByStatus ({ commit, state }, params) {
      commit('setLoading', true, { root: true })

      await PaApi().delete(
        `/admin/newsletters/${params.newsletterId}/delete_recipients_by_status/${params.status}`
      ).then((resp) => {
        commit('storeEmailRecipient', resp)
      }).finally(() => {
        commit('setLoading', false, { root: true })
      })
    },

    async deleteRecipientsByStatus ({ commit, state }, params) {
      commit('setLoading', true, { root: true })

      await PaApi().delete(
        `/admin/targeted_emails/${params.emailId}/delete_recipients_by_status/${params.status}`
      ).then((resp) => {
        commit('storeEmailRecipient', resp)
      }).finally(() => {
        commit('setLoading', false, { root: true })
      })
    },

    async resendEmails ({ commit, state }, params = {
      emails: [],
      unrestricted: false,
      type: 'newsletters'
    }) {
      commit('setLoading', true, { root: true })

      await PaApi().post(
        `/admin/${this.getters['EmailModule/modelPath']}/${state.email.id}/email`,
        {
          emails: params.emails,
          unrestricted: params.unrestricted
        }
      ).finally(() => {
        commit('setLoading', false, { root: true })
      })
    },

    /*
      This is a debug feature, otherwise it'd be tied to
      /admin/emails/:id/recipients
    */
    async createEmailRecipient ({ commit, state }, formdata) {
      commit('setLoading', true, { root: true })

      await PaApi().post(
        '/admin/email_recipients', formdata
      ).then((resp) => {
        commit('storeEmailRecipient', resp)
      }).finally(() => {
        commit('setLoading', false, { root: true })
      })
    }
  },

  mutations: {
    publish (state) {
      state.email.inactive = true
    },

    storeAttachment (state, payload) {
      state.attachment = processSuccess(payload, baseOpts)
    },

    storeEmail (state, payload) {
      // Eventually all PaApi requests will use the baseOpts recursive option.
      baseOpts.recursive = true

      state.email = processSuccess(payload, baseOpts)

      // force initialized emails to be inactive.
      // @todo Newsletter sniffing will be phased out.
      if (!parseInt(state.email.id) && !state.email.newsletter_type) {
        state.email.inactive = true
      }

      state.email.send_at = state.email.send_at || defaultSendAt()
    },

    storeEmailRecipient (state, payload) {
      state.emailRecipient = processSuccess(payload, baseOpts)
    },

    storeEmailRecipients (state, payload) {
      state.emailRecipients = processSuccess(payload, baseOpts)
    },

    storeEmails (state, payload) {
      state.emails = processSuccess(payload, baseOpts)
    },

    storeSendAt (state, sendAt) {
      state.email.send_at = sendAt
    },

    storeStats (state, payload) {
      state.stats = payload.data.data
    },

    storeEmailPuzzle (state, puzzle) {
      state.email.puzzles.push(puzzle)
    }
  }
}



// WEBPACK FOOTER //
// ./src/modules/EmailModule.js